import { inject, Injectable, Query } from '@angular/core';
import { IRepoService } from './repo.service.interface';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';

import { DiscoveryService } from './discovery-service';
import { MessageService } from './message.service';
import { PaginationService } from './pagination.service';
import { ProcessDefinition } from './process-definition';
import { ProcessDefinitionResponse } from './process-definition-response';
import { ProcessInstance } from './process-instance';
import { ProfileService } from './profile.service';
import { QueryMetaData } from './query-metadata';
import { FormDefinition } from './form-definition';

@Injectable({
  providedIn: 'root'
})
export class RepoService implements IRepoService {

  private paginationService = inject(PaginationService);
  private discoveryService = inject(DiscoveryService);
  private profileService = inject(ProfileService);
  private http = inject(HttpClient);
  private messageService = inject(MessageService);

  /** Log a RepoService message with the MessageService */
  private log(message: string) {
    this.messageService.set(`${message}`);
  }

  getProcessDefinitionCount(): Observable<number> {
    const query = { start: 0, size: 1 };
    return this.http.get<ProcessDefinitionResponse>( this.discoveryService.getProcessDefinitionsUrl(query), {headers: this.getHttpHeaders()} )
      .pipe(
        tap(x => this.log(`Found ${x.total} service${x.total > 1 ? 's' : ''} you can request`)),
        map(x => x.total), /* store only the total count */
        catchError(this.handleError<number>('getProcessDefinitionCount', 0))
      );
  }

  getProcessDefinitions(query: QueryMetaData): Observable<ProcessDefinition[]> {
    return this.http.get<ProcessDefinitionResponse>( this.discoveryService.getProcessDefinitionsUrl(query), {headers: this.getHttpHeaders()} )
      .pipe(
        tap(x => this.log(`Found ${x.total} service${x.total > 1 ? 's' : ''} you can request`)),
        tap(x => this.paginationService.paginate('/process-starters', { total: x.total, start: x.start, size: query.size} as QueryMetaData)),
        map(x => x.data), /* flowable response includes metadata, just want data */
        catchError(this.handleError<ProcessDefinition[]>('getProcessDefinitions', []))
      );
  }

  getProcessDefinition(procDefId: string): Observable<ProcessDefinition> {
    this.log(`Showing details of ${procDefId}`);
    return this.http.get<ProcessDefinition>( this.discoveryService.getProcessDefinitionUrl(procDefId), {headers: this.getHttpHeaders()} )
      .pipe(
        tap(x => this.log(`Found definition '${x.name}'`)),
        catchError(this.handleError<ProcessDefinition>('getProcessDefinition', undefined))
      );
  }

  getFormDefinition(procDefId: string): Observable<FormDefinition> {
    return this.http.get<FormDefinition>( this.discoveryService.getProcessStartFormUrl(procDefId), {headers: this.getHttpHeaders()} )
      .pipe(
        tap(x => this.log(`Fill out ${x.name.toLowerCase()}`)),
        catchError(this.handleError<FormDefinition>('getFormDefinition'))
      );
  }

  startInstance(procDefId: string, formDef?: FormDefinition): Observable<ProcessInstance> {
    this.log(`Starting new instance of ${procDefId}`);
    const procInst = {
      processDefinitionId: procDefId,
      businessKey: 'myBusinessKey',
      returnVariables: true,
      variables: []
    };
    if (formDef !== undefined) {
      procInst.variables = formDef.fields.map(obj => {
        return { name: obj.id, value: obj.value };
      });
    }
    return this.http.post<ProcessInstance>( this.discoveryService.getProcessInstancesUrl(), procInst, {headers: this.getHttpHeaders()} )
      .pipe(
        tap(() => this.log(`Started instance of ${procDefId}`)),
        catchError(this.handleError<ProcessInstance>('startInstance', procInst))
      );
  }

  private getHttpHeaders(): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/json');
    return headers.append('Authorization', 'Basic ' + btoa(this.profileService.getUsername() + ':' + this.profileService.getToken()));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
